import * as THREE from 'three';
import { lerp } from '@/core/math';
import type { ItemSocket, Rig, RigKind, RigPose } from './Rig';
import { FlashlightRig, WeaponRig } from './WeaponRig';

export { IDLE_HANDS } from './Rig';
export type { RigKind, RigPose } from './Rig';

const PLAYER_COLORS = { skin: 0x9a7d66, top: 0x3d4a52, bottom: 0x2a2c30, boots: 0x1c1c1c };
const THREAT_COLORS = { skin: 0x7c7a70, top: 0x4a4038, bottom: 0x2e2a26, boots: 0x1a1816 };
/** Arm raised almost horizontal: the gun points where the camera looks. */
const ARM_RAISED = -1.5;
const ARM_LOWERED_READY = -0.35;
const ARM_LENGTH = 0.58;

/**
 * Procedural humanoid (PLACEHOLDER_ART). Torso, head and four limbs with a simple gait cycle;
 * threats carry a slumped posture and a raised-arm attack telegraph so they read from silhouette.
 * The player rig carries hand sockets: the held weapon or medkit in the right hand, the torch in
 * the left, with raise, reload and pitch-follow motion driven from the pose. Kept as the fallback
 * when the character assets fail to load.
 */
export class CharacterRig implements Rig {
  readonly group = new THREE.Group();
  onFootstep: ((foot: 'left' | 'right') => void) | null = null;
  private readonly torso: THREE.Mesh;
  private readonly head: THREE.Mesh;
  private readonly armL: THREE.Group;
  private readonly armR: THREE.Group;
  private readonly legL: THREE.Group;
  private readonly legR: THREE.Group;
  private readonly body = new THREE.Group();
  private readonly handR = new THREE.Object3D();
  private readonly handL = new THREE.Object3D();
  private readonly weapon: WeaponRig | null = null;
  private readonly torch: FlashlightRig | null = null;
  private phase = 0;
  private readonly materials: THREE.Material[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];

  constructor(readonly kind: RigKind) {
    const colors = kind === 'player' ? PLAYER_COLORS : THREAT_COLORS;
    const make = (w: number, h: number, d: number, color: number): THREE.Mesh => {
      const geometry = new THREE.BoxGeometry(w, h, d);
      const material = new THREE.MeshStandardMaterial({ color, roughness: 0.85 });
      this.geometries.push(geometry);
      this.materials.push(material);
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      return mesh;
    };
    this.torso = make(0.42, 0.62, 0.24, colors.top);
    this.torso.position.y = 1.18;
    this.head = make(0.22, 0.24, 0.22, colors.skin);
    this.head.position.y = 1.64;
    const hips = make(0.38, 0.2, 0.22, colors.bottom);
    hips.position.y = 0.78;
    this.armL = this.limb(make(0.11, ARM_LENGTH, 0.11, colors.top), 0.3, 1.45);
    this.armR = this.limb(make(0.11, ARM_LENGTH, 0.11, colors.top), -0.3, 1.45);
    this.legL = this.limb(make(0.14, 0.72, 0.14, colors.bottom), 0.11, 0.72);
    this.legR = this.limb(make(0.14, 0.72, 0.14, colors.bottom), -0.11, 0.72);
    const bootL = make(0.15, 0.1, 0.24, colors.boots);
    bootL.position.set(0, -0.72, 0.04);
    this.legL.add(bootL);
    const bootR = make(0.15, 0.1, 0.24, colors.boots);
    bootR.position.set(0, -0.72, 0.04);
    this.legR.add(bootR);
    this.body.add(this.torso, this.head, hips, this.armL, this.armR, this.legL, this.legR);
    this.group.add(this.body);
    if (kind === 'threat') this.body.rotation.x = 0.16;
    if (kind === 'player') {
      // Hand sockets sit at the end of each arm, rotated so an item's natural +Z runs along the arm.
      this.handR.position.set(0, -ARM_LENGTH + 0.04, 0.03);
      this.handR.rotation.x = Math.PI / 2;
      this.handL.position.set(0, -ARM_LENGTH + 0.04, 0.03);
      this.handL.rotation.x = Math.PI / 2;
      this.armR.add(this.handR);
      this.armL.add(this.handL);
      this.weapon = new WeaponRig();
      this.handR.add(this.weapon.group);
      this.torch = new FlashlightRig();
      this.torch.group.visible = false;
      this.handL.add(this.torch.group);
    }
  }

  private limb(mesh: THREE.Mesh, x: number, y: number): THREE.Group {
    const pivot = new THREE.Group();
    pivot.position.set(x, y, 0);
    mesh.geometry.computeBoundingBox();
    mesh.position.y = -(mesh.geometry.boundingBox?.max.y ?? 0.3);
    pivot.add(mesh);
    return pivot;
  }

  /** One-shots are implied by the pose in the procedural rig. */
  trigger(): void {
    // nothing to do
  }

  /** The procedural hands have fixed sockets; the tuner only applies to the skinned rig. */
  setSocket(_item: 'pistol' | 'medkit' | 'flashlight', _socket: ItemSocket): void {
    // nothing to do
  }

  /** World position of the muzzle (player rig only); falls back to the group position. */
  muzzleWorldPosition(out: THREE.Vector3): THREE.Vector3 {
    if (this.weapon) return this.weapon.muzzle.getWorldPosition(out);
    return out.copy(this.group.position);
  }

  update(pose: RigPose, dt: number): void {
    this.group.position.set(pose.x, pose.y, pose.z);
    this.group.rotation.y = pose.yaw;
    if (pose.dead) {
      this.animateDeath(pose.deathTimer, dt);
      return;
    }
    const stride = pose.moving ? Math.min(1.4, pose.speed / 2.6) : 0;
    this.phase += dt * (this.kind === 'threat' ? 6 : 8) * Math.max(0.3, stride);
    const swing = Math.sin(this.phase) * 0.7 * stride;
    this.legL.rotation.x = swing;
    this.legR.rotation.x = -swing;
    if (this.kind === 'threat') this.animateThreat(pose, swing * 0.35);
    else this.animatePlayerArms(pose, swing * 0.8);
    this.body.position.y = Math.abs(Math.sin(this.phase)) * 0.04 * stride;
    this.body.rotation.z = pose.hurt ? 0.08 : 0;
  }

  private animateThreat(pose: RigPose, armSwing: number): void {
    const raise = pose.attack > 0 ? lerp(0.6, 2.2, pose.attack) : 0.55 + 0.1 * Math.sin(this.phase * 0.5);
    this.armL.rotation.x = -raise + armSwing;
    this.armR.rotation.x = -raise - armSwing;
    this.body.rotation.x = pose.stagger ? -0.25 : 0.16 + (pose.attack > 0 ? 0.25 * pose.attack : 0);
  }

  /** Weapon raise blends the right arm from a ready carry to the aim line; the left hand supports or reloads. */
  private animatePlayerArms(pose: RigPose, armSwing: number): void {
    const raise = pose.aiming ? Math.max(pose.weaponRaise, 0.5) : pose.weaponRaise;
    const holding = pose.equipped === 'pistol';
    // Holding the pistol: low-ready carry with a faint walk swing so the weapon stays in view.
    const restR = holding ? ARM_LOWERED_READY - armSwing * 0.25 : -armSwing;
    const aimedR = ARM_RAISED - pose.lookPitch;
    let armR = lerp(restR, aimedR, raise);
    let armLX = pose.flashlightOn ? -0.9 : armSwing;
    let armLZ = pose.flashlightOn ? 0.15 : 0;
    let handRoll = 0;
    if (raise > 0.5) {
      armLX = lerp(armLX, -1.35, (raise - 0.5) * 2);
      armLZ = lerp(armLZ, 0.35, (raise - 0.5) * 2);
    }
    if (pose.reloadProgress > 0) {
      // Reload: the gun rolls toward the chest while the left hand works the magazine.
      const bump = Math.sin(pose.reloadProgress * Math.PI);
      armR = lerp(armR, -1.05, bump);
      armLX = lerp(armLX, -1.15, bump);
      armLZ = lerp(armLZ, 0.55, bump);
      handRoll = bump * 0.7;
    }
    this.armR.rotation.x = armR;
    this.armL.rotation.x = armLX;
    this.armL.rotation.z = armLZ;
    this.handR.rotation.z = handRoll;
    this.weapon?.setEquipped(pose.equipped);
    if (this.torch) {
      this.torch.group.visible = pose.flashlightOn;
      this.torch.setLit(pose.flashlightOn);
    }
  }

  private animateDeath(timer: number, _dt: number): void {
    const t = Math.min(1, timer / 1.2);
    const eased = 1 - (1 - t) * (1 - t);
    this.body.rotation.x = lerp(this.kind === 'threat' ? 0.16 : 0, -Math.PI / 2 + 0.08, eased);
    this.body.position.y = lerp(0, 0.28, eased) - 0.72 * eased;
    this.armL.rotation.x = 0.4;
    this.armR.rotation.x = -0.3;
    this.legL.rotation.x = 0.1;
    this.legR.rotation.x = -0.15;
  }

  dispose(): void {
    this.weapon?.dispose();
    this.torch?.dispose();
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    this.group.removeFromParent();
  }
}
